import { Box, HStack, Icon, Skeleton, Stack, Text } from '@chakra-ui/react';
import { Validation } from '@m/icons';

export const SectionHeader = ({
  children,
  dataTestId,
  title,
  padding = 4,
}: {
  children?: React.ReactNode;
  dataTestId?: string;
  padding?: number;
  title: React.ReactNode;
}) => (
  <Stack py={padding} gap={2} data-testid={dataTestId}>
    <Text as="span" variant="Subheader" fontWeight="600" color="text.primary">
      {title}
    </Text>
    {children && (
      <Text as="span" variant="SmallBodyRegular" color="text.secondary">
        {children}
      </Text>
    )}
  </Stack>
);

export const ListRow = ({
  icon,
  title,
  children,
  rightContent,
  loading,
}: {
  children?: React.ReactNode;
  icon?: React.ReactNode;
  loading?: boolean;
  rightContent?: React.ReactNode;
  title: React.ReactNode;
}) => {
  if (loading) {
    return (
      <HStack py={4} gap={4}>
        <Skeleton boxSize="20px" flexShrink={0} />
        <Stack flex={1} gap={1} minW={0}>
          <Skeleton height="4" width="80%" />
          <Skeleton height="3" width="60%" />
        </Stack>
        <Skeleton height="4" width="12" flexShrink={0} />
      </HStack>
    );
  }
  return (
    <HStack align="center" py={4} gap={4} width="100%">
      <Box fontSize="20px" color="fill.icon.descriptive">
        {icon}
      </Box>
      <Stack align="start">
        <Text variant="MainBodyMedium" color="text.primary" marginTop={children ? undefined : 1}>
          {title}
        </Text>
        {children &&
          (typeof children === 'string' ? (
            <Text variant="MainBodyRegular" color="text.secondary">
              {children}
            </Text>
          ) : (
            children
          ))}
      </Stack>
      {rightContent && <Box ml="auto">{rightContent}</Box>}
    </HStack>
  );
};

export const CostRow = ({
  amount,
  dataTestId,
  description,
  icon,
  info,
  isTotal,
  isValidation,
  loading,
  priceDetails,
  title,
}: {
  amount: React.ReactNode;
  dataTestId?: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  info?: React.ReactNode;
  isTotal?: boolean;
  isValidation?: boolean;
  loading?: boolean;
  priceDetails?: React.ReactNode;
  title: React.ReactNode;
}) => {
  if (loading) {
    return (
      <Stack gap={1} py={2} data-testid={dataTestId}>
        <HStack justifyContent="space-between">
          <HStack gap={1} flex={1}>
            <Skeleton boxSize="20px" flexShrink={0} />
            <Skeleton height="4" flex={1} maxW="24" />
          </HStack>
          <Skeleton height="4" width="14" flexShrink={0} />
        </HStack>
        <HStack justifyContent="space-between">
          <Skeleton height="3" width="20" />
          <Skeleton height="3" width="16" />
        </HStack>
      </Stack>
    );
  }
  return (
    <Stack gap={1} py={2} data-testid={dataTestId}>
      <HStack justifyContent="space-between" color="text.primary">
        <HStack gap={1}>
          {isValidation ? <Icon as={Validation} color="fill.icon.green" /> : icon}
          <Text as="span" variant={isTotal ? 'MainBodyDemiBold' : 'MainBodyRegular'}>
            {title}
          </Text>
          {info}
        </HStack>
        <Text as="span" variant={isTotal ? 'MainBodyDemiBold' : 'MainBodyRegular'}>
          {amount}
        </Text>
      </HStack>
      {(description || priceDetails) && (
        <HStack justifyContent="space-between" color="text.secondary">
          <Text as="span" variant="SmallBodyRegular">
            {description}
          </Text>
          <Text as="span" variant="SmallBodyRegular">
            {priceDetails}
          </Text>
        </HStack>
      )}
    </Stack>
  );
};

export const Note = ({ children }: { children: React.ReactNode }) => (
  <Text py={2} variant="SmallBodyRegular" color="text.secondary">
    {children}
  </Text>
);

export const PageTitle = ({
  action,
  dataTestId,
  padding = 4,
  subheader,
  title,
  topPadding,
}: {
  action?: React.ReactNode;
  dataTestId?: string;
  padding?: number;
  subheader?: React.ReactNode;
  title: React.ReactNode;
  topPadding?: boolean;
}) => (
  <Stack
    data-testid={dataTestId}
    gap={2}
    pb={padding}
    pt={topPadding ? padding + 2 : padding}
    px={padding}
  >
    <HStack align="center" gap={2} width="100%">
      <Stack flex={1} gap={1} minW={0}>
        <Text as="span" variant="Header.H3" color="text.primary">
          {title}
        </Text>
        {subheader && (
          <Text as="span" variant="MainBodyRegular" color="text.secondary">
            {subheader}
          </Text>
        )}
      </Stack>
      {action}
    </HStack>
  </Stack>
);
